import { Link } from "react-router-dom";
import { ArrowRight, FileText, Camera, Receipt, CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { HOW_IT_WORKS } from "@/sections/HowItWorksSection";
import { track } from "@/lib/analytics";

const DELIVERABLES = [
  { Icon: FileText, text: "A clear scope, service charges, expected costs and timeline — in writing" },
  { Icon: Camera, text: "Photo and video documentation of inspections and completed work" },
  { Icon: Receipt, text: "Itemised expenses and invoices, maintained as property records" },
  { Icon: CheckCircle2, text: "Approval checkpoints before any major expense" },
];

export default function HowItWorks() {
  return (
    <>
      <SEO
        title="How It Works | NRI Property Services Hyderabad"
        description="Six steps to a managed Hyderabad property: tell us about your property, free assessment, inspection, customized proposal, owner approval, and we take care of it."
        path="/how-it-works"
      />
      <PageHero
        eyebrow="How It Works"
        titleLines={["From First Call to", "Fully Managed — in Six Steps"]}
        copy="A structured onboarding designed for owners who cannot be physically present in Hyderabad."
      />

      <section className="bg-ivory py-24 sm:py-32">
        <div className="container-site max-w-4xl">
          <div className="relative">
            <div className="absolute bottom-10 left-[27px] top-10 w-px bg-gold-500/30" aria-hidden="true" />
            <Stagger className="space-y-10" gap={0.12}>
              {HOW_IT_WORKS.map((s) => (
                <StaggerItem key={s.n}>
                  <div className="relative flex gap-6 sm:gap-8" data-testid={`hiw-step-${s.n}`}>
                    <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold-500/50 bg-white font-display text-sm font-extrabold text-gold-600 shadow-sm">
                      {s.n}
                    </span>
                    <div className="card-lift flex-1 rounded-2xl border border-navy-900/10 bg-white p-6 sm:p-8">
                      <h3 className="font-display text-xl font-extrabold tracking-tight text-navy-900">{s.title}</h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground sm:text-base">{s.copy}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="bg-navy-900 py-24 sm:py-32">
        <div className="container-site">
          <SectionHeading
            eyebrow="What You Receive"
            dark
            title="Clarity at Every Step."
          />
          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2" gap={0.08}>
            {DELIVERABLES.map(({ Icon, text }) => (
              <StaggerItem key={text}>
                <div className="flex h-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-500/15">
                    <Icon className="h-5 w-5 text-gold-400" />
                  </span>
                  <p className="text-sm font-medium leading-relaxed text-white/80">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <div className="mt-12">
              <Link
                to="/contact"
                className="btn-gold"
                data-testid="hiw-start-cta"
                onClick={() => track("assessment_request", { location: "how_it_works" })}
              >
                Start With Step 1 <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
